/**
 * Dashboard Component
 * Main landing page showing progress, skills, and available scenarios
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Dashboard.css';
import { useProgress } from '../../contexts/ProgressContext';
import { Target, TrendingUp, Award, BarChart3, Users, Code, Briefcase, TrendingDown, Lock } from 'lucide-react';
import { canAccessScenario } from '../../utils/scenarioSequencing';

const Dashboard = () => {
  const { progress, loading, loadProgress } = useProgress();
  const [previousSkills, setPreviousSkills] = useState(null);

  // Refetch progress when dashboard mounts so completed scenarios and skills are up to date
  useEffect(() => {
    if (loadProgress) loadProgress();
  }, [loadProgress]);

  useEffect(() => {
    // Load previous skills from localStorage
    const saved = localStorage.getItem('previousSkills');
    if (saved) {
      try {
        setPreviousSkills(JSON.parse(saved));
      } catch (e) {
        console.error('Error loading previous skills:', e);
      }
    }
  }, []);

  useEffect(() => {
    // Save current skills for next comparison
    if (progress?.skills) {
      localStorage.setItem('previousSkills', JSON.stringify(progress.skills));
    }
  }, [progress?.skills]);

  if (loading) {
    return <div className="dashboard-loading">Loading...</div>;
  }

  const scenarios = [
    { id: 'saas-1', title: 'Discovery & Definition', track: 'SaaS', difficulty: 'Beginner', description: 'Leadership wants to add a new feature based on competitor analysis. Your task is to validate the problem and define success metrics.' },
    { id: 'saas-2', title: 'Agile Sprint Planning', track: 'SaaS', difficulty: 'Beginner', description: 'Convert your product brief into actionable sprint work. Work with engineering to estimate and prioritize.' },
    { id: 'saas-3', title: 'Design Collaboration', track: 'SaaS', difficulty: 'Intermediate', description: 'Turn user stories into concrete designs. Balance user needs with technical constraints.' },
    { id: 'saas-4', title: 'Development & Daily Standups', track: 'SaaS', difficulty: 'Intermediate', description: 'Feature is in development. Manage daily progress, unblock engineers, and handle trade-offs.' },
    { id: 'saas-5', title: 'Launch & Metrics Analysis', track: 'SaaS', difficulty: 'Advanced', description: 'Feature has launched. Analyze results, gather feedback, and decide on next steps.' },
  ];

  const completedCount = progress?.completedScenarios?.length || 0;
  const totalScore = progress?.totalScore || 0;
  const skills = progress?.skills || {
    dataLiteracy: 0,
    stakeholderManagement: 0,
    technicalDepth: 0,
    businessAcumen: 0
  };

  const isCompleted = (scenarioId) => {
    return progress?.completedScenarios?.some(s => s.id === scenarioId);
  };

  const getSkillPercentage = (value) => {
    return Math.min(100, Math.round((value / 100) * 100));
  };

  const getSkillChange = (skillName) => {
    if (!previousSkills) return null;
    const current = skills[skillName] || 0;
    const previous = previousSkills[skillName] || 0;
    const change = current - previous;
    
    // Only show change if significant (more than 1%)
    if (Math.abs(change) < 1) return null;
    
    return {
      value: Math.abs(change),
      direction: change > 0 ? 'up' : 'down'
    };
  };

  const SkillItem = ({ name, value, icon: Icon }) => {
    const change = getSkillChange(name);
    const percentage = getSkillPercentage(value);
    
    return (
      <div className="skill-item">
        <div className="skill-header">
          <Icon size={18} />
          <span>{name}</span>
          {change && (
            <span className={`skill-change ${change.direction}`}>
              {change.direction === 'up' ? (
                <TrendingUp size={14} />
              ) : (
                <TrendingDown size={14} />
              )}
              {change.value.toFixed(1)}%
            </span>
          )}
        </div>
        <div className="skill-bar">
          <div 
            className="skill-progress" 
            style={{ width: `${percentage}%` }}
          />
        </div>
        <div className="skill-value">{percentage}%</div>
      </div>
    );
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Welcome to gongfu</h1>
      </div>

      <div className="dashboard-stats">
        <div className="stat-card">
          <div className="stat-icon">
            <Target size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{completedCount}</div>
            <div className="stat-label">Scenarios Completed</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <Award size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{totalScore.toFixed(0)}</div>
            <div className="stat-label">Total Score</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <TrendingUp size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{scenarios.length}</div>
            <div className="stat-label">Available Scenarios</div>
          </div>
        </div>
      </div>

      <div className="dashboard-skills">
        <h2>Your Skills</h2>
        <div className="skills-grid">
          <SkillItem 
            name="Data Literacy" 
            value={skills.dataLiteracy} 
            icon={BarChart3} 
          />
          <SkillItem 
            name="Stakeholder Management" 
            value={skills.stakeholderManagement} 
            icon={Users} 
          />
          <SkillItem 
            name="Technical Depth" 
            value={skills.technicalDepth} 
            icon={Code} 
          />
          <SkillItem 
            name="Business Acumen" 
            value={skills.businessAcumen} 
            icon={Briefcase} 
          />
        </div>
      </div>

      <div className="dashboard-scenarios">
        <h2>Available Scenarios</h2>
        
        <div className="scenarios-section">
          <h3>SaaS Product Development Track</h3>
          <div className="scenarios-grid">
            {scenarios.filter(s => s.track === 'SaaS').map(scenario => {
              const accessCheck = canAccessScenario(scenario.id, progress?.completedScenarios || []);
              const isLocked = !accessCheck.accessible;
              
              if (isLocked) {
                return (
                  <div
                    key={scenario.id}
                    className="scenario-card locked"
                    title={accessCheck.reason || 'Complete previous scenarios first'}
                  >
                    <div className="scenario-header">
                      <h4>{scenario.title}</h4>
                      <span className={`difficulty-badge ${scenario.difficulty.toLowerCase()}`}>
                        {scenario.difficulty}
                      </span>
                    </div>
                    <p className="scenario-description">{scenario.description}</p>
                    <div className="scenario-locked">
                      <Lock size={16} />
                      <span>Locked - Complete previous scenarios</span>
                    </div>
                  </div>
                );
              }
              
              return (
                <Link
                  key={scenario.id}
                  to={`/scenario/${scenario.id}`}
                  className={`scenario-card ${isCompleted(scenario.id) ? 'completed' : ''}`}
                >
                  <div className="scenario-header">
                    <h4>{scenario.title}</h4>
                    <span className={`difficulty-badge ${scenario.difficulty.toLowerCase()}`}>
                      {scenario.difficulty}
                    </span>
                  </div>
                  <p className="scenario-description">{scenario.description}</p>
                  {isCompleted(scenario.id) && (
                    <div className="scenario-completed">
                      <Award size={16} />
                      <span>Completed</span>
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
